import React, { useEffect } from 'react';
import {
	type Rendered,
	type ThreeLOPromptProps as PublicThreeLOPromptProps,
	type RenderFn,
} from '@atlassian/forge-ui-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import { cssMap } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import SectionMessage from '@atlaskit/section-message/message';
import Button from '@atlaskit/button/default/button';
import { useAuth } from './useAuth';

const styles = cssMap({
	box: {
		borderColor: token('color.border.warning'),
		borderStyle: 'solid',
		borderWidth: token('border.width.selected'),
	},
});

export type ThreeLOPromptProps = Rendered<PublicThreeLOPromptProps> &
	Dependencies & { onInitialization?: () => void; onSuccess: () => Promise<void | (() => void)> };

interface Dependencies {
	appName?: string;
	metadata?: {
		moduleType?: string;
	};
}

const ThreeLOPrompt = (props: ThreeLOPromptProps): React.JSX.Element => {
	const { loading, startAuth, promptMessage, buttonText } = useAuth(props);
	const { authUrl, onInitialization } = props;

	useEffect(() => {
		if (onInitialization) {
			onInitialization();
		}
	}, [onInitialization]);

	if (!authUrl || authUrl === '') {
		return (
			<SectionMessage appearance="error">
				<FormattedMessage {...messages.noAuthURLErrorMessage} />
			</SectionMessage>
		);
	}

	return (
		<React.Fragment>
			{/* Prevents clicks anywhere in the prompt area (text, padding, whitespace)
			   from bubbling up to parent containers that may dismiss or act on click events. */}
			<Box
				onPointerDown={(e: { stopPropagation: () => void }) => e.stopPropagation()}
				padding="space.250"
				xcss={styles.box}
			>
				<Stack alignInline="start" space="space.250" grow="hug" testId="three-lo-prompt">
					<Box>{promptMessage}</Box>
					<Button onClick={startAuth} isLoading={loading}>
						{buttonText}
					</Button>
				</Stack>
			</Box>
		</React.Fragment>
	);
};

export default ThreeLOPrompt;

export const ThreeLOPromptRenderFn: RenderFn = ({ forgeDoc, dispatch }) => {
	const { message, authUrl, promptText } = forgeDoc.props as PublicThreeLOPromptProps;
	return (
		<ThreeLOPrompt
			message={message}
			authUrl={authUrl}
			promptText={promptText}
			onSuccess={() => dispatch({ type: 'render', extensionData: {} })}
		/>
	);
};

export const makeThreeLOPrompt = ({
	appName,
	onSuccess,
}: Dependencies & { onSuccess?: () => void }): RenderFn => {
	return function threeLOPrompt({ forgeDoc: { props }, dispatch }) {
		const { message, authUrl, promptText } = props as PublicThreeLOPromptProps;
		return (
			<ThreeLOPrompt
				message={message}
				authUrl={authUrl}
				promptText={promptText}
				appName={appName}
				onSuccess={() => {
					if (onSuccess) {
						onSuccess();
					}
					return dispatch({ type: 'render', extensionData: {} });
				}}
			/>
		);
	};
};

type ThreeLOPromptPropsForCustomUI = PublicThreeLOPromptProps &
	Dependencies & {
		onInitialization?: () => void;
		onSuccess: () => Promise<void | (() => void)>;
	};

export const makeThreeLOPromptForCustomUI = ({
	appName,
	onSuccess,
	message,
	onInitialization,
}: Dependencies & { message?: string; onInitialization?: () => void; onSuccess?: () => void }) => {
	return function threeLOPrompt(props: ThreeLOPromptPropsForCustomUI): React.JSX.Element {
		const { authUrl, promptText, onSuccess: onSuccessFromIframeComponent } = props;
		return (
			<ThreeLOPrompt
				message={message}
				authUrl={authUrl}
				promptText={promptText}
				appName={appName}
				onSuccess={() => {
					if (onSuccess) {
						onSuccess();
					}

					return Promise.resolve(onSuccessFromIframeComponent);
				}}
				onInitialization={onInitialization}
			/>
		);
	};
};

const messages = defineMessages({
	noAuthURLErrorMessage: {
		id: 'confluence.threeLOPrompt.no.authURL.error-message',
		defaultMessage: 'Consent URL not found. Please refresh the page and try again.',
		description:
			'This text is displayed when the URL that gives an app certain permissions is not present.',
	},
});
